import { ReactNode } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export type StatTrend = 'up' | 'down' | 'neutral';

export interface StatCardProps {
  emoji: string;
  label: ReactNode;
  value: ReactNode;
  /** Gradient top bar — [from, to]. Default: accent-primary → accent-secondary. */
  accentColors?: [string, string];
  /** Klasa Tailwind dla koloru value (np. 'text-success' dla pozytywnych KPI). */
  valueColor?: string;
  delta?: { value: ReactNode; trend?: StatTrend };
  caption?: ReactNode;
  /** Dla kosztów ustawiamy false — wtedy up=danger. */
  positiveTrendIsGood?: boolean;
  className?: string;
  onClick?: () => void;
}

const TREND_ICON: Record<StatTrend, typeof TrendingUp> = {
  up:      TrendingUp,
  down:    TrendingDown,
  neutral: Minus,
};

/**
 * KPI card zgodny ze wzorem z FinanceDashboardPage:
 * - gradient top bar (accentColors)
 * - duża emoji w tle (overflow za krawędź, subtle float + pulse animation)
 * - foreground emoji tile (kwadrat 44×44 z subtelnym tłem via border-subtle)
 * - label mały + value duży (flex row)
 * - hover zwiększa opacity background emoji
 *
 * Legacy components dashboard (np. invoice-repair/InvoiceRepairModal StatCard)
 * mają własną lokalną implementację - tego komponentu używaj do NOWYCH KPI.
 */
export function StatCard({
  emoji,
  label,
  value,
  accentColors = ['#4a9eff', '#6b8cce'],
  valueColor = 'text-text-primary',
  delta,
  caption,
  positiveTrendIsGood = true,
  className,
  onClick,
}: StatCardProps) {
  const trend = delta?.trend ?? 'neutral';
  const TrendIcon = TREND_ICON[trend];
  const trendColor =
    trend === 'neutral'
      ? 'text-text-muted'
      : (trend === 'up') === positiveTrendIsGood
        ? 'text-success'
        : 'text-danger';

  const interactive = Boolean(onClick);

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
      className={[
        'relative bg-surface-raised/50 border border-border-subtle rounded-card overflow-hidden transition-all duration-normal ease-smooth animate-slide-up group',
        interactive ? 'hover:border-accent-primary/30 cursor-pointer' : 'hover:border-border',
        className,
      ].filter(Boolean).join(' ')}
    >
      {/* Gradient top bar - animated gradient shimmer */}
      <div
        className="h-1 animate-gradient bg-[length:200%_100%]"
        style={{
          background: `linear-gradient(90deg, ${accentColors[0]}, ${accentColors[1]}, ${accentColors[0]})`,
          backgroundSize: '200% 100%',
        }}
      />

      {/* Background emoji — widoczniejsze niż wcześniej (było 0.07/0.14,
         user zgłosił że na białym słabo widać). Teraz 0.14 default,
         0.28 na hover. Dark i light działają. */}
      <div className="absolute -right-3 bottom-1/2 translate-y-1/2 pointer-events-none select-none leading-none opacity-[0.14] group-hover:opacity-[0.28] transition-opacity duration-slow">
        <span className="text-[6.5rem] inline-block animate-subtle-float">{emoji}</span>
      </div>

      {/* Content */}
      <div className="p-5 flex items-center gap-4 relative z-10">
        {/* Foreground emoji tile */}
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-surface-hover/50 border border-border-subtle text-2xl leading-none">
          {emoji}
        </div>

        {/* Text */}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-text-muted mb-0.5 truncate">{label}</p>
          <p className={`text-xl font-bold truncate ${valueColor}`}>{value}</p>
          {delta && (
            <div className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${trendColor}`}>
              <TrendIcon className="w-3.5 h-3.5" />
              <span>{delta.value}</span>
            </div>
          )}
          {caption && !delta && (
            <p className="mt-0.5 text-xs text-text-muted leading-snug truncate">{caption}</p>
          )}
        </div>
      </div>
    </div>
  );
}
